import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { DEFAULT_VACCINE_SUBTYPE_ID } from "@/hooks/useProductSubtypes";
import { quotedIlikePattern } from "@/lib/utils/postgrest-filters";
import { toast } from "sonner";
import type { DoseIntervalUnit } from "@/lib/dose-interval";
import type { ItemDoseInterval } from "@/hooks/useItems";

type ApplicationProductType = "vacina" | "medicamento";
type ProductSubtypeRow = { id?: string; name?: string | null } | null;
type PackageSubtypeRow = { id?: string; name?: string | null } | null;
type ApplicationDoseIntervalRow = {
  id: string;
  item_id: string;
  next_application_item_id: string | null;
  dose_interval_value: number | null;
  dose_interval_unit: string | null;
  sort_order: number;
};
type ApplicationCatalogItemRow = {
  id: string;
  name: string | null;
  manufacturer: string | null;
  is_active: boolean | null;
  deleted_at: string | null;
  customer_id: string;
  type: string;
  dose_intervals: ApplicationDoseIntervalRow[] | null;
  next_application_defaults: Array<{ item_id: string; interval_days: number }> | null;
  product_subtype?: ProductSubtypeRow;
  package_subtype?: PackageSubtypeRow;
};

export type VaccineOption = {
  kind: "item";
  value: string;
  label: string;
  rawName: string;
  itemId: string;
  manufacturer?: string | null;
  applicationType?: ApplicationProductType | null;
  dose_intervals: ItemDoseInterval[];
  next_application_defaults?: Array<{ item_id: string; interval_days: number }> | null;
  deleted_at?: string | null;
  isDeleted?: boolean;
  isActive?: boolean | null;
};

interface UseApplicationsOptions {
  searchTerm?: string;
  speciesId?: string;
  isActive?: boolean;
  clinicId?: string;
  includeItemIds?: string[];
}

const toPascalCase = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(
      /(^|[\s/-])([a-zà-ÿ])/g,
      (_match, separator: string, char: string) => `${separator}${char.toUpperCase()}`,
    );

const normalizeSubtype = (value?: string | null): ApplicationProductType | null => {
  const subtype = value?.trim().toLowerCase();
  if (subtype === "vacina" || subtype === "medicamento") return subtype;
  return null;
};

export const filterApplicationOptionsBySpeciesId = <T extends { kind: string; speciesIds?: string[] }>(
  options: T[],
  speciesId?: string,
) => {
  if (!speciesId) return options;
  return options.filter((opt) => {
    if (opt.kind !== "medical_product") return true;
    if (!opt.speciesIds) return true;
    return opt.speciesIds.includes(speciesId);
  });
};

const normalizeApplicationItemOption = (item: ApplicationCatalogItemRow): VaccineOption | null => {
  const rawName = item.name ?? "";
  const applicationType = normalizeSubtype(
    item.type === "PACOTE" ? item.package_subtype?.name : item.product_subtype?.name,
  );
  if (!applicationType) return null;

  return {
    kind: "item",
    value: `item:${item.id}`,
    label: toPascalCase(rawName),
    rawName,
    itemId: item.id,
    manufacturer: item.manufacturer,
    applicationType,
    dose_intervals: (item.dose_intervals ?? [])
      .map((row) => ({
        id: row.id,
        item_id: row.item_id,
        next_application_item_id: row.next_application_item_id,
        dose_interval_value: row.dose_interval_value,
        dose_interval_unit: row.dose_interval_unit as DoseIntervalUnit | null,
        sort_order: row.sort_order,
      }))
      .sort((a, b) => a.sort_order - b.sort_order),
    next_application_defaults: (item.next_application_defaults as Array<{ item_id: string; interval_days: number }> | null) ?? null,
    deleted_at: item.deleted_at,
    isDeleted: Boolean(item.deleted_at),
    isActive: item.is_active,
  };
};

export const useApplications = ({ searchTerm, isActive, clinicId, includeItemIds }: UseApplicationsOptions = {}) => {
  const { data: customerId } = useCurrentClinicCustomerId();
  const normalizedIncludeItemIds = (includeItemIds ?? []).filter(Boolean).sort();

  return useQuery({
    queryKey: ["applications-catalog", customerId, clinicId, searchTerm, isActive, normalizedIncludeItemIds],
    enabled: !!customerId || !!clinicId,
    queryFn: async () => {
      let effectiveCustomerId = customerId;
      if (!effectiveCustomerId) {
        if (!clinicId) throw new Error("Nenhuma clínica selecionada");
        const { data, error } = await supabase
          .from("clinics")
          .select("customer_id")
          .eq("id", clinicId)
          .single();
        if (error) throw error;
        effectiveCustomerId = data.customer_id;
      }

      let query = supabase
        .from("items")
        .select("id, name, manufacturer, is_active, deleted_at, customer_id, type, next_application_defaults, dose_intervals:item_dose_intervals!item_dose_intervals_item_id_fkey(id, item_id, next_application_item_id, dose_interval_value, dose_interval_unit, sort_order), product_subtype:product_subtypes(id, name), package_subtype:package_subtypes(id, name)")
        .eq("customer_id", effectiveCustomerId)
        .is("deleted_at", null);

      if (typeof isActive === "boolean") {
        query = query.eq("is_active", isActive);
      }

      if (searchTerm && searchTerm.length >= 2) {
        const p = quotedIlikePattern(searchTerm);
        query = query.or(`name.ilike.${p},manufacturer.ilike.${p}`);
      }

      const { data, error } = await query.order("name");
      if (error) throw error;

      const itemsById = new Map<string, ApplicationCatalogItemRow>();
      ((data ?? []) as ApplicationCatalogItemRow[]).forEach((item) => {
        itemsById.set(item.id, item);
      });

      const missingIncludedIds = normalizedIncludeItemIds.filter((id) => !itemsById.has(id));
      if (missingIncludedIds.length > 0) {
        const { data: includedData, error: includedError } = await supabase
          .from("items")
          .select("id, name, manufacturer, is_active, deleted_at, customer_id, type, next_application_defaults, dose_intervals:item_dose_intervals!item_dose_intervals_item_id_fkey(id, item_id, next_application_item_id, dose_interval_value, dose_interval_unit, sort_order), product_subtype:product_subtypes(id, name), package_subtype:package_subtypes(id, name)")
          .eq("customer_id", effectiveCustomerId)
          .in("id", missingIncludedIds);

        if (includedError) throw includedError;
        ((includedData ?? []) as ApplicationCatalogItemRow[]).forEach((item) => {
          itemsById.set(item.id, item);
        });
      }

      return Array.from(itemsById.values())
        .map(normalizeApplicationItemOption)
        .filter((option): option is VaccineOption => Boolean(option));
    },
  });
};

export const useVaccines = useApplications;
export const filterVaccineOptionsBySpeciesId = filterApplicationOptionsBySpeciesId;

export const useCreateApplication = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      name: string;
      manufacturer?: string | null;
      speciesId?: string;
      applicationType?: ApplicationProductType;
      next_application_defaults?: Array<{ item_id: string; interval_days: number }> | null;
    }) => {
      const customerId = await getCurrentClinicCustomerId();

      const timestamp = Date.now();
      const sku = `APP-${timestamp}`;
      const productType = payload.applicationType ?? "vacina";

      let productSubtypeId: string | undefined =
        productType === "vacina" ? DEFAULT_VACCINE_SUBTYPE_ID : undefined;

      const { data: subtype } = await supabase
        .from("product_subtypes")
        .select("id, name")
        .ilike("name", `%${productType}%`)
        .or(`customer_id.is.null,customer_id.eq.${customerId}`)
        .limit(1)
        .maybeSingle();

      if (subtype?.id) {
        productSubtypeId = subtype.id;
      }

      const { data: item, error } = await (supabase as typeof supabase & {
        from: (table: "items") => {
          insert: (value: unknown) => {
            select: (query: string) => {
              single: () => Promise<{ data: ApplicationCatalogItemRow; error: Error | null }>;
            };
          };
        };
      })
        .from("items")
        .insert({
          type: "PRODUTO",
          name: payload.name.trim(),
          sku,
          unit: "DOSE",
          product_subtype_id: productSubtypeId,
          track_by_batch: true,
          track_by_expiry: true,
          is_perishable: true,
          manufacturer: payload.manufacturer?.trim() || null,
          next_application_defaults: payload.next_application_defaults ?? [],
          is_active: true,
          customer_id: customerId,
        })
        .select("id, name, manufacturer, is_active, deleted_at, customer_id, next_application_defaults, dose_intervals:item_dose_intervals!item_dose_intervals_item_id_fkey(id, item_id, next_application_item_id, dose_interval_value, dose_interval_unit, sort_order), product_subtype:product_subtypes(id, name)")
        .single();

      if (error) throw error;
      return item;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["applications-catalog"] });
      queryClient.invalidateQueries({ queryKey: ["vaccines"] });
      queryClient.invalidateQueries({ queryKey: ["items"] });
      toast.success("Aplicação criada com sucesso");
    },
    onError: (error: Error) => {
      console.error("Erro ao criar aplicação:", error);
      toast.error("Erro ao criar aplicação");
    },
  });
};

export const useCreateVaccine = useCreateApplication;
